import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { reduxForm, Field, Fields } from 'redux-form/immutable';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import { selectUser } from 'entities/user/selectors';
import Box from 'retired/Box';
import FormTextField from 'retired/FormTextField';
import FormPasswordField from 'retired/FormPasswordField';
import FormFieldLabel from 'retired/FormFieldLabel';
import OrganizationMenu from 'retired/OrganizationMenu';
import { required, isEmail, minLength } from 'utils/validators';
import { useQueryParams } from 'utils/useQueryParams';
import { useInjectEpics } from 'utils/injectEpics';
import config from 'config';
import { Privileges } from 'utils/privileges';
import User from 'entities/user/model';
import FormCurrencySelect from 'retired/FormCurrencySelect';
import { matchCompanySso } from 'containers/LoginPage/utils';

import { FORM_IDENTIFIER } from './constants';
import * as Actions from './actions';
import epics from './epics';
import DemoForm from './DemoForm';
import NewCompanyForm from './NewCompanyForm';

const requiredValidator = required();
const isEmailValidator = isEmail();
const minLength8Validator = minLength(8);

const key = 'userPage';

type Props = {
  actions: object;
  handleSubmit: () => void;
  submitting: boolean;
  user: User;
};

const UserPage = ({ actions, handleSubmit, submitting, user }: Props) => {
  const isUserAdmin = user?.type === 'Admin';
  const queryParams = useQueryParams();

  useInjectEpics({ key, epics });
  useEffect(() => {
    actions.initAction();
  }, []);

  const submit = (data) => {
    actions.saveAction({ ...data.toJS() });
  };

  // @TODO-DP: replace this with this code after `user.provider` becomes available
  // const isSso = user?.provider !== 'local';
  const parent = user?.organization?.parent;
  const parentSsoConfig = parent && typeof parent !== 'string' && parent.sso2;

  const organizerSsoEnabled =
    (parentSsoConfig && parentSsoConfig.organizerSsoEnabled) ||
    user?.organization?.sso2?.organizerSsoEnabled;

  const isDeedNonSsoEmployee =
    user.email.endsWith('@joindeed.com') && user.email.match(/^[^@\s]*\+[^@\s]*@joindeed\.com$/i);

  const isSso =
    organizerSsoEnabled &&
    matchCompanySso(user.email, [parentSsoConfig ? parent : user.organization]);

  const switchTo = queryParams.get('switchTo');
  if (isUserAdmin && switchTo && switchTo !== user?.organization?.id && !submitting) {
    submit(user.set('organization', switchTo));
  }

  return (
    <>
      {(user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS) ||
        user?.hasPrivilege(Privileges.ORGANIZATION_VIEW_ORGANIZERS)) && (
        <div style={{ marginBottom: 60 }}>
          <OrganizationMenu user={user} />
        </div>
      )}
      <Box style={{ maxWidth: 1200 }}>
        <Helmet title="User Settings" />
        {!user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS) && <h1>My Profile</h1>}
        <form onSubmit={handleSubmit(submit)}>
          <fieldset>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormFieldLabel forName="email">Change Login</FormFieldLabel>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  component={FormTextField}
                  required
                  fullWidth
                  label="Email"
                  validate={[requiredValidator, isEmailValidator]}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <FormFieldLabel forName="firstName">Change Name</FormFieldLabel>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Field
                      id="firstName"
                      name="fullName.first"
                      component={FormTextField}
                      required
                      fullWidth
                      label="First name"
                      validate={requiredValidator}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      id="lastName"
                      name="fullName.last"
                      component={FormTextField}
                      required
                      fullWidth
                      label="Last name"
                      validate={requiredValidator}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <br />
            <br />
            {(!isSso || isDeedNonSsoEmployee) && (
              <>
                <FormFieldLabel forName="oldPassword">Change Password</FormFieldLabel>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Field
                      id="oldPassword"
                      name="oldPassword"
                      type="password"
                      autoComplete="current-password"
                      component={FormTextField}
                      fullWidth
                      label="Old Password"
                      validate={minLength8Validator}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Fields
                      names={['newPassword', 'newPassword2']}
                      component={FormPasswordField}
                      userInputs={{ currentUser: user }}
                      errorText="Password fields must match."
                      firstFieldProps={{
                        name: 'newPassword',
                        autoComplete: 'new-password',
                        label: 'New Password',
                      }}
                      secondFieldProps={{
                        name: 'newPassword2',
                        autoComplete: 'new-password',
                        label: 'Confirm Password',
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            <br />
            <br />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormFieldLabel forName="displayCurrency">Change Display Currency</FormFieldLabel>
                <br />
                <Field id="displayCurrency" name="displayCurrency" component={FormCurrencySelect} />
              </Grid>
            </Grid>
            <br />
            <br />
            <Typography align="right">
              <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                Submit
              </Button>
            </Typography>
          </fieldset>
        </form>
      </Box>
      {user?.isAdmin && !config.isProduction && <DemoForm />}
      {user?.isAdmin && <NewCompanyForm />}
    </>
  );
};

const withConnect = connect(
  (state) => ({ user: selectUser(state) }),
  (dispatch) => ({ actions: bindActionCreators(Actions, dispatch) })
);
const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});
export default compose(withConnect, withForm)(UserPage);
